import withDiscountAutoApply from 'HOCs/withDiscountAutoApply';
import withDiscountEligibilityToast, {
  DISCOUNT_ELIGIBILITY_TOAST_TYPE,
} from 'HOCs/withDiscountEligibilityToast';
import DiscountsDrawer from 'components/Discount/components/DiscountsDrawer';
import { isSystemDiscountFailure } from 'components/Discount/components/DiscountsDrawer/displayState';
import { getIsDefaultAutoApplyPending } from 'components/Discount/components/DiscountsDrawer/isDefaultAutoApplyPending';
import { DiscountModal } from 'components/Discount/components/DiscountModal/ver2';
import {
  DISCOUNT_LOADING_MIN_VISIBLE_MS,
  useMinVisibleFlag,
} from 'components/Discount/hooks/useMinVisibleFlag';
import {
  getDiscountDefault,
  shouldShowTransactionDiscountEligibilityToast,
  useTransactionDiscountRecovery,
} from 'components/Discount/func';
import {
  getPayoutItem,
  getReceiveLimitsWithPayoutMethod,
} from 'components/FormAddTransaction/func';
import ButtonCommon from 'components/common/ButtonCommon';
import { checkFormInputValidation } from 'components/common/func';
import {
  ADD_TRANSACTION_AMOUNT_ANOTHER_FIELDS,
  ADD_TRANSACTION_AMOUNT_FIELDS,
  ADD_TRANSACTION_METHOD_PICKUP_NAME,
  ADD_TRANSACTION_PAYOUT_METHOD_NAME,
  ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME,
  ADD_TRANSACTION_RECEIVER_GET_NAME,
  ADD_TRANSACTION_STEP_REFERENCE,
  ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME,
  ADD_TRANSACTION_YOU_SEND_NAME,
  DESKTOP_MIN_WIDTH,
  METHOD_HOME,
  METHOD_PICKUP,
  RESET_ADD_TRANSACTION_INFORMATION_STORE,
  SCREEN_ID_REMITTANCE_STEP_1,
  SCREEN_ID_REMITTANCE_STEP_2,
} from 'constants';
import { format } from 'helpers';
import { useDiscountLoadingState } from 'hooks/discount-loading/DiscountLoadingContext';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useReceiver from 'hooks/receiver/useReceiver';
import useAuth from 'hooks/useAuth';
import useServiceAlertsUtil from 'hooks/service-alerts/useServiceAlertsUtil';
import useAutoShowEventModal from 'hooks/useAutoShowEventModal';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useScrollIntoViewDelay from 'hooks/useScrollIntoViewDelay';
import useStore from 'hooks/useStore';
import useValidationToCreateATransactionOnHoliday from 'hooks/useValidationToCreateATransactionOnHoliday';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import {
  SET_ADD_TRANSACTION_CURRENT_STEP,
  SET_ADD_TRANSACTION_INFORMATION,
} from 'store/action';
import styled from 'styled-components';
import BodySectionHeader from '../../BodySectionHeader';
import CalculatorForm from '../../CalculatorForm/ver2';
// import FormInputAddAnotherReceivingCurrencySwitch from '../../FormInputAddAnotherReceivingCurrencySwitch';
// import FormInputAmountAnotherValidation from '../../FormInputAmountAnotherValidation';
import useClarity from 'hooks/clarity/useClarity';
import FormInputAmountValidation from '../../FormInputAmountValidation';
import SizeBox from 'components/common/SizeBox';

const initialFormField = RESET_ADD_TRANSACTION_INFORMATION_STORE.remittance;

// Time isDiscountSettled must stay continuously true before discounts are
// treated as settled by downstream consumers. It briefly flickers true between
// discount sub-phases (rate fetch -> auto-apply -> revalidation); this hold
// bridges those inter-phase gaps so the service-alerts observer does not act on
// a premature "settled" and surface its popup mid-loading.
const DISCOUNT_SETTLE_STABLE_MS = 800;

const SCREEN_ID = SCREEN_ID_REMITTANCE_STEP_2;
const DiscountsDrawerExt = withDiscountAutoApply(DiscountsDrawer);

const Remittance = withDiscountEligibilityToast(
  ({
    countryConfig,
    handleOnToggleCancelPopup,
    isDisabled,
    renderDiscountEligibilityToast,
    setShowDiscountEligibilityToast,
    handleOnClickDiscountEligibilityToastCloseButton,
    isForceHideDiscountEligibilityToast,
  }) => {
    const { t } = useLang();
    const { token } = useAuth();
    const { state, dispatch } = useStore();
    const { isReceiverOfBusinessType, isReceiverOfIndividualType } =
      useReceiver();

    const [information, setInformation] = useState(initialFormField);
    const [validation, setValidation] = useState(initialFormField);
    const [isFetching, setFetching] = useState(false);
    const [isChecking, setChecking] = useState(false);
    const [isDiscountLoadingHidden, setDiscountLoadingHidden] = useState(true);
    const [currentRateValue, setCurrentRateValue] = useState(0);
    const [currentRateValueAnother, setCurrentRateValueAnother] = useState(0);
    const [isShowCalculatorForm, setShowCalculatorForm] = useState(false);
    // False on every fresh visit to step 2. Flips true only once the discount
    // state has settled for this visit (see the settle effect below), so the
    // available/empty discount section is not shown instantly on landing.
    const [isDiscountResultReady, setDiscountResultReady] = useState(false);
    // False until the mount effect below re-hydrates local `information` from the
    // store. Gates the discount recovery hook so it does not act on the transient
    // where `information` is still initialFormField (amounts 0) right after a
    // remount (e.g. returning to Step 2 from Step 4) — which otherwise triggered a
    // spurious client re-derive that cleared the applied discount and re-armed
    // auto-apply (a visible discount "re-check" on Back).
    const [isDiscountRecoveryReady, setDiscountRecoveryReady] = useState(false);
    const {
      currentUser,
      addTransactionInformation,
      addTransactionCurrentStep: currentStep,
      currentReceiver,
    } = state;
    const { receiverPayoutMethod, remittance, paymentMethod } =
      addTransactionInformation || {};

    const formInputAmountRef = useRef(null);
    const formInputAmountAnotherRef = useRef(null);

    const {
      isAddAnotherReceivingCurrency,
      currentCurrencySelected,
      currentCurrencySelectedAnother,
      feeAmount,
    } = information || {};

    const isNextDisabled = isFetching;
    const { isPreventNextStep2 } = useServiceAlertsUtil();
    const { isMakePayment } = useFormAddTransactionStore();
    const { isDesktop } = useDetectDevice();
    const {
      feeAmount: feeAmountStore,
      currentCurrencySelected: currentCurrencySelectedStore,
      currentCurrencySelectedAnother: currentCurrencySelectedAnotherStore,
      currentRateStore,
      currentRateAnotherStore,
      rate,
      rateAnother,
      isAddAnotherReceivingCurrency: isAddAnotherReceivingCurrencyStore,
    } = remittance || {};
    const { discountApplied } = paymentMethod || {};
    const discountDefault = getDiscountDefault({
      discountsAvailable: paymentMethod?.discountsAvailable,
      discountsInactive: paymentMethod?.discountsInactive,
    });

    const payoutMethod =
      receiverPayoutMethod[ADD_TRANSACTION_PAYOUT_METHOD_NAME];
    const isPayoutMethodByHome = payoutMethod === METHOD_HOME;
    const isPayoutMethodByPickup = payoutMethod === METHOD_PICKUP;

    const receiveLimits = getReceiveLimitsWithPayoutMethod({
      dmCode: payoutMethod,
      pickupOfficeId: isPayoutMethodByPickup
        ? receiverPayoutMethod[ADD_TRANSACTION_METHOD_PICKUP_NAME]?.pickupOffice
            ?.pickUpOfficeId
        : null,
      isReceiverOfBusinessType,
      isReceiverOfIndividualType,
      receiveLimits: countryConfig?.receiveLimits,
      dmList: countryConfig?.dmList,
    });

    const receiverAmount =
      Number(information?.[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0) || '';
    const receiverAmountAnother =
      Number(information?.[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME] || 0) ||
      '';

    const {
      isOpenHolidayUrgentPopup,
      setOpenHolidayUrgentPopup,
      HolidayUrgentPopup,
    } = useValidationToCreateATransactionOnHoliday({
      isDisabled,
      countryCode: countryConfig?.countryCode,
      payoutMethod,
      payoutItem: getPayoutItem(receiverPayoutMethod, payoutMethod),
      currencyCode:
        countryConfig?.countryCode === 'VNM'
          ? currentCurrencySelected?.currencyCode === 'VND' ||
            currentCurrencySelectedAnother?.currencyCode === 'VND'
            ? 'VND'
            : ''
          : countryConfig?.countryCode === 'PHL'
            ? currentCurrencySelected?.currencyCode === 'PHP' ||
              currentCurrencySelectedAnother?.currencyCode === 'PHP'
              ? 'PHP'
              : ''
            : '',
      receiverAmount:
        countryConfig?.countryCode === 'VNM'
          ? currentCurrencySelected?.currencyCode === 'VND'
            ? receiverAmount
            : currentCurrencySelectedAnother?.currencyCode === 'VND'
              ? receiverAmountAnother
              : 0
          : countryConfig?.countryCode === 'PHL'
            ? currentCurrencySelected?.currencyCode === 'PHP'
              ? receiverAmount
              : currentCurrencySelectedAnother?.currencyCode === 'PHP'
                ? receiverAmountAnother
                : 0
            : 0,
    });

    // scroll to calculator form
    const validRef = useRef(null);
    useScrollIntoViewDelay(
      validRef,
      isAddAnotherReceivingCurrency && validRef,
      [isAddAnotherReceivingCurrency],
    );

    const { clarityAPI } = useClarity();
    useEffect(() => {
      const customUserIdIdentify = currentUser?.senderId;
      if (!customUserIdIdentify) return;
      clarityAPI.identify(
        customUserIdIdentify,
        '',
        SCREEN_ID_REMITTANCE_STEP_1,
      );
    }, [currentUser]);

    const checkCurrencySelectedCorresponding = (
      currencySelected,
      receiveLimits,
    ) => {
      if (!currencySelected) return false;

      return receiveLimits?.find(
        (rl) => rl.currencyCode === currencySelected.currencyCode,
      );
    };

    const isShowToggleButtonToAddAnotherCurrency =
      isPayoutMethodByHome &&
      isMakePayment &&
      isAddAnotherReceivingCurrencyStore;

    const { setDiscountSettled } = useDiscountLoadingState();

    const {
      autoApplyPolicy,
      canViewFailedDiscountConditions,
      failedDiscount,
      failedDiscountErrorCode,
      handleAmountChangeStart,
      handleApplyDiscount,
      handleCloseFailedDiscountConditions,
      handleOpenFailedDiscountConditions,
      handleRetryFailedDiscount,
      hasActiveFetchFailed,
      onRetryActiveListFetch,
      isApplyingDiscount,
      isRevalidationPending,
      isCurrentDiscountsLoaded,
      isOpenFailedDiscountModal,
    } = useTransactionDiscountRecovery({
      countryConfig,
      // Source the fetch-key context (currency + single-currency flag) from the
      // committed store slice, not the local `information` state. Local
      // `information` resets to initialFormField on every remount (steps unmount)
      // and is only re-synced by a mount effect, so reading it here produced a
      // transient/mismatched fetch key on returning to Step 2 (e.g. from Step 4),
      // which fired a spurious getDiscountsActive refetch. The store slice is the
      // stable committed context the persisted discountFetchKey was built from.
      // Amounts stay from `information` — they only drive the client-side
      // re-filter, which never calls the server.
      currentCurrencySelected: currentCurrencySelectedStore,
      currentReceiver,
      information,
      isAddAnotherReceivingCurrency: isAddAnotherReceivingCurrencyStore,
      isDisabled,
      // No forceRefetch on return to Step 2: the discount fetch key now includes
      // receiver identity, so a Step-1 change (including a same-city receiver
      // swap) changes the key and refetches on its own, while returning with no
      // change reuses the cached active list instead of refetching.
      isReady: isDiscountRecoveryReady,
      paymentMethod,
      payoutMethod,
      setFetching,
      token,
    });
    // A backend/system failure is surfaced by the discount failure section, not
    // the eligibility toast; only a coded (ineligibility) failure shows the
    // APPLY_FAIL toast.
    const systemDiscountFailure = isSystemDiscountFailure(
      failedDiscount,
      failedDiscountErrorCode,
    );
    const discountEligibilityToastType =
      failedDiscount && !systemDiscountFailure
        ? DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_FAIL
        : discountApplied
          ? DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_SUCCESS
          : DISCOUNT_ELIGIBILITY_TOAST_TYPE.ELIGIBILITY;
    // The Send-Money flow never shows the pure ELIGIBILITY nudge (only
    // APPLY_SUCCESS / APPLY_FAIL). The hide-on-ELIGIBILITY show effect below does
    // this asynchronously, which leaves a one-frame flash on a currency change:
    // clearing failedDiscount to re-validate the default flips the type to
    // ELIGIBILITY for a render while the toast is still shown, briefly painting the
    // eligibility message before the re-validation re-fails. Gate the render on the
    // type synchronously so that transient ELIGIBILITY frame never paints.
    const isEligibilityNudgeToast =
      discountEligibilityToastType ===
      DISCOUNT_ELIGIBILITY_TOAST_TYPE.ELIGIBILITY;
    const isDefaultDiscountApplyPending = getIsDefaultAutoApplyPending({
      discountsLoaded: isCurrentDiscountsLoaded,
      hasDefault: Boolean(discountDefault),
      isApplied: Boolean(discountApplied),
      hasFailure: Boolean(failedDiscount),
      isBlocked: autoApplyPolicy.isBlocked,
      // isDisabled / isReady intentionally omitted: the Send-Money flow has no
      // such guards today, so their neutral values (false / true) preserve
      // current behaviour. Reconciling these across flows is a separate,
      // behaviour-changing slice (see .repo-tmp).
    });

    const hasAmount = Boolean(
      Number(information[ADD_TRANSACTION_YOU_SEND_NAME] || 0) ||
      Number(information[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0) ||
      Number(information[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0) ||
      Number(information[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME] || 0),
    );

    // Mirror of the DiscountLoading overlay's visibility: same signal
    // (isApplyingDiscount) run through the same shared minimum-visible hold, so
    // this stays true for the full loader duration even after a fast apply
    // finishes. This is the exact flag the Discount Available section gates on
    // (DiscountsDrawer's isLoadingSectionActive), so the calculator and the
    // drawer reveal/hide in lockstep instead of the calculator appearing while
    // the loader is still on screen.
    const isDiscountLoaderVisible = useMinVisibleFlag(isApplyingDiscount);

    // Live "discount settled" signal — the UNLATCHED form of the
    // isDiscountResultReady condition below, plus a wait for the DiscountLoading
    // overlay to fully hide. It is true only while no default auto-apply, manual
    // apply, or rate fetch is in flight AND the loader's minimum-visible hold has
    // elapsed. Unlike isDiscountResultReady (which latches true after the first
    // settle and stays true), this drops back to false during every
    // re-validation, so the calculator's "applied" text/line hides while a
    // discount is re-applying, amounts are recalculating, or the loading overlay
    // is still visible, then returns once the result settles and the loader is
    // gone — matching the Discount Available section.
    // Make Payment: discount recovery/auto-apply are disabled (isDisabled), so
    // isCurrentDiscountsLoaded never turns true and the unlatched signal below
    // would stay false, hiding the calculator's "applied" text/line on Step 2
    // even though the created txn's discount is hydrated and shown read-only in
    // the section above. Nothing is in flight here, so treat "a discount is
    // present" as settled — matching Steps 3/4 (which default isDiscountSettled
    // to true). The applied text/line still gate on the numeric discount amount.
    const isDiscountSettled = isMakePayment
      ? !isEmpty(discountApplied)
      : isCurrentDiscountsLoaded &&
        !isDefaultDiscountApplyPending &&
        !isApplyingDiscount &&
        // A context-change revalidation is carried but not yet confirmed: do not
        // show the premature applied badge/line for the stale-but-retained
        // discountApplied until revalidation settles.
        !isRevalidationPending &&
        !isFetching &&
        !isDiscountLoaderVisible;

    // The raw isDiscountSettled flag briefly flickers true in the gaps between
    // discount sub-phases (rate fetch -> auto-apply -> revalidation), so treat
    // discounts as settled only after it has stayed true continuously for a
    // short hold. Any return to busy resets the hold.
    const [isDiscountSettledStable, setDiscountSettledStable] = useState(false);
    useEffect(() => {
      if (!isDiscountSettled) {
        setDiscountSettledStable(false);
        return undefined;
      }
      const timer = setTimeout(
        () => setDiscountSettledStable(true),
        DISCOUNT_SETTLE_STABLE_MS
      );
      return () => clearTimeout(timer);
    }, [isDiscountSettled]);

    // Publish the stable "discount settled" signal so sibling features rendered
    // outside this subtree (the service-alerts observer) can defer their popups
    // until discount validation and the Discount Loading overlay have finished.
    // Reset to settled on unmount (leaving Step 2 / remount) so a stale "busy"
    // never persists and permanently suppresses service-alert popups.
    useEffect(() => {
      setDiscountSettled(isDiscountSettledStable);
    }, [isDiscountSettledStable, setDiscountSettled]);

    useEffect(
      () => () => {
        setDiscountSettled(true);
      },
      [setDiscountSettled]
    );

    // Reveal the available/empty discount section only after the discount
    // state has settled on this visit. The amount is carried from step 1, so
    // "an amount exists" is true on landing and cannot gate this; instead we
    // wait until discounts are loaded, any default auto-validation has finished
    // (not pending, not applying), and the rate/calculator is idle. If no
    // auto-validation runs, this settles as soon as those conditions hold after
    // mount. Once ready it stays ready; subsequent re-validation is handled by
    // the drawer's own loading gate.
    useEffect(() => {
      if (!isCurrentDiscountsLoaded) return;
      if (isDefaultDiscountApplyPending || isApplyingDiscount) return;
      if (isFetching) return;
      setDiscountResultReady(true);
    }, [
      isCurrentDiscountsLoaded,
      isDefaultDiscountApplyPending,
      isApplyingDiscount,
      isFetching,
    ]);

    useEffect(() => {
      let newInformation = { ...remittance };

      const sendAmount = Number(remittance[ADD_TRANSACTION_YOU_SEND_NAME] || 0);
      const getReceiverAmount = Number(
        remittance[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0,
      );

      const sendAnotherAmount = Number(
        remittance[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
      );
      const getReceiverAnotherAmount = Number(
        remittance[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME] || 0,
      );

      newInformation = {
        ...newInformation,
        [ADD_TRANSACTION_YOU_SEND_NAME]: sendAmount,
        [ADD_TRANSACTION_RECEIVER_GET_NAME]: getReceiverAmount,
        currentRateStore,
        currentCurrencySelected:
          currentCurrencySelectedStore || receiveLimits?.[0],
        feeAmount: feeAmountStore,
      };

      const isResetCurrencyAnother =
        !isAddAnotherReceivingCurrencyStore &&
        currentRateAnotherStore &&
        currentCurrencySelectedAnotherStore;

      if (isPayoutMethodByHome && !isResetCurrencyAnother) {
        if (
          checkCurrencySelectedCorresponding(
            currentCurrencySelectedStore,
            receiveLimits,
          )
        ) {
          newInformation = {
            ...newInformation,
            currentCurrencySelected:
              currentCurrencySelectedStore || receiveLimits?.[0],
          };
        }

        newInformation = {
          ...newInformation,
          [ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME]: sendAnotherAmount,
          [ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME]: getReceiverAnotherAmount,
          currentCurrencySelectedAnother: currentCurrencySelectedAnotherStore,
          currentRateAnotherStore,
          isAddAnotherReceivingCurrency: isAddAnotherReceivingCurrencyStore,
        };
      } else {
        newInformation = {
          ...newInformation,
          [ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME]: 0,
          [ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME]: 0,
          currentCurrencySelectedAnother: null,
          currentRateAnotherStore: null,
          isAddAnotherReceivingCurrency: false,
        };
      }

      setCurrentRateValue(
        isDisabled ? rate?.rateValue : currentRateStore?.rateValue,
      );

      if (isAddAnotherReceivingCurrencyStore) {
        setCurrentRateValueAnother(
          isDisabled
            ? rateAnother?.rateValue
            : currentRateAnotherStore?.rateValue,
        );
      }

      newInformation = {
        ...newInformation,
        currentRate: currentRateStore?.rateValue,
      };

      setInformation(newInformation);
      // `information` now reflects the committed store values for this visit, so
      // the discount recovery may safely run without seeing the reset transient.
      setDiscountRecoveryReady(true);
    }, []);

    useEffect(() => {
      const sendAmount =
        Number(information[ADD_TRANSACTION_YOU_SEND_NAME] || 0) || '';
      const receiverAmount =
        Number(information[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0) ||
        (sendAmount ? 0 : '');

      document.getElementsByName(ADD_TRANSACTION_YOU_SEND_NAME)[0].value =
        sendAmount ? format.toAmountStr(sendAmount) : sendAmount;
      document.getElementsByName(ADD_TRANSACTION_RECEIVER_GET_NAME)[0].value =
        receiverAmount ? format.toAmountStr(receiverAmount) : receiverAmount;

      if (isAddAnotherReceivingCurrency) {
        const sendAmountAnother =
          Number(information[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0) || '';
        const receiverAmountAnother =
          Number(information[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME] || 0) ||
          '';

        document.getElementsByName(
          ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME,
        )[0].value = sendAmountAnother
          ? format.toAmountStr(sendAmountAnother)
          : sendAmountAnother;
        document.getElementsByName(
          ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME,
        )[0].value = receiverAmountAnother
          ? format.toAmountStr(receiverAmountAnother)
          : receiverAmountAnother;
      }
    }, [information, isAddAnotherReceivingCurrency]);

    useEffect(() => {
      if (isApplyingDiscount) {
        setDiscountLoadingHidden(false);
        return undefined;
      }

      const hideTimer = setTimeout(() => {
        setDiscountLoadingHidden(true);
      }, DISCOUNT_LOADING_MIN_VISIBLE_MS);

      return () => clearTimeout(hideTimer);
    }, [isApplyingDiscount]);

    const isToastEligibleToShow = shouldShowTransactionDiscountEligibilityToast(
      {
        hasAmount,
        isShowCalculatorForm,
        isCurrentDiscountsLoaded,
        isApplyingDiscount,
        isRevalidationPending,
        isDiscountLoadingHidden,
        discountDefault,
        failedDiscount,
        discountApplied,
        isSystemDiscountFailure: systemDiscountFailure,
      },
    );

    // The pure ELIGIBILITY nudge is intentionally suppressed in the Send-Money
    // flow; only APPLY_SUCCESS / APPLY_FAIL toasts fire here. The explicit hide
    // on the ELIGIBILITY branch also covers a success/fail toast that is already
    // visible when the type reverts to ELIGIBILITY (e.g. the applied discount is
    // removed), which nothing else resets. isRevalidationPending hides the same
    // way when a context-change revalidation starts (e.g. a currency switch): the
    // applied discount is carried, so the type stays APPLY_SUCCESS and the
    // ELIGIBILITY branch does not fire, yet the stale success toast must close
    // until revalidation confirms. This mirrors the amount path, which closes the
    // toast explicitly in handleDiscountAmountChangeStart. Soft dismiss only (no
    // force-hide ref), so a re-confirmed apply re-shows a fresh toast.
    useEffect(() => {
      if (
        discountEligibilityToastType ===
          DISCOUNT_ELIGIBILITY_TOAST_TYPE.ELIGIBILITY ||
        isRevalidationPending
      ) {
        setShowDiscountEligibilityToast(false);
        return;
      }
      if (isToastEligibleToShow) {
        setShowDiscountEligibilityToast(true);
      }
    }, [
      isToastEligibleToShow,
      discountEligibilityToastType,
      isRevalidationPending,
      setShowDiscountEligibilityToast,
    ]);

    // Auto-close the success (3s) and failure (5s) toasts. The eligibility toast
    // is not auto-closed. Armed only while the toast is eligible to show; the
    // cleanup clears the timer if the type changes, it stops being eligible, or
    // the component unmounts. Soft dismiss (setShow only) — it does not set the
    // force-hide ref, unlike the explicit close button.
    useEffect(() => {
      if (!isToastEligibleToShow) return;
      let autoCloseMs = null;
      if (
        discountEligibilityToastType ===
        DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_SUCCESS
      ) {
        autoCloseMs = 3000;
      } else if (
        discountEligibilityToastType ===
        DISCOUNT_ELIGIBILITY_TOAST_TYPE.APPLY_FAIL
      ) {
        autoCloseMs = 5000;
      }
      if (autoCloseMs === null) return;
      const timer = setTimeout(
        () => setShowDiscountEligibilityToast(false),
        autoCloseMs,
      );
      return () => clearTimeout(timer);
    }, [
      isToastEligibleToShow,
      discountEligibilityToastType,
      setShowDiscountEligibilityToast,
    ]);

    // Re-enable the eligibility toast once a discount validation finishes, so an
    // explicit close is not permanent. Edge-guarded on the isApplyingDiscount
    // true->false transition (NOT just !isApplyingDiscount): returning to Step 2
    // without a fresh validation produces no edge, so an explicitly-closed toast
    // (which set the page-level force-hide ref) stays hidden; a completed
    // validation clears the ref and the show effect above re-shows it.
    const wasApplyingDiscountRef = useRef(false);
    useEffect(() => {
      const justCompletedValidation =
        wasApplyingDiscountRef.current && !isApplyingDiscount;
      wasApplyingDiscountRef.current = isApplyingDiscount;
      if (
        justCompletedValidation &&
        isForceHideDiscountEligibilityToast?.current
      ) {
        isForceHideDiscountEligibilityToast.current = false;
      }
    }, [isApplyingDiscount, isForceHideDiscountEligibilityToast]);

    const handleDiscountAmountChangeStart = () => {
      setShowDiscountEligibilityToast(false);
      handleAmountChangeStart();
    };

    const handleNextStep = () => {
      let newInformation = { ...information };
      let newValidation = { ...validation };

      newValidation =
        formInputAmountRef.current.handleGetReceiverAmountValidation(
          newInformation,
          newValidation,
        );

      if (isAddAnotherReceivingCurrency) {
        newValidation =
          formInputAmountAnotherRef.current.handleGetReceiverAmountValidation(
            newInformation,
            newValidation,
          );
      }

      setValidation(newValidation);

      delete newInformation['remittanceId'];
      delete newValidation['remittanceId'];

      delete newInformation['remittanceAnotherId'];
      delete newValidation['remittanceAnotherId'];

      delete newInformation['rate'];
      delete newValidation['rate'];

      delete newInformation['rateAnother'];
      delete newValidation['rateAnother'];

      delete newInformation['isAddAnotherReceivingCurrency'];
      delete newValidation['isAddAnotherReceivingCurrency'];

      delete newInformation['currentCurrencySelected'];
      delete newValidation['currentCurrencySelected'];

      delete newInformation['currentCurrencySelectedAnother'];
      delete newValidation['currentCurrencySelectedAnother'];

      delete newInformation['currentRateStore'];
      delete newValidation['currentRateStore'];

      delete newInformation['currentRateAnotherStore'];
      delete newValidation['currentRateAnotherStore'];

      delete newInformation['feeAmount'];
      delete newValidation['feeAmount'];

      if (!isAddAnotherReceivingCurrency) {
        delete newInformation[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME];
        delete newValidation[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME];
        delete newInformation[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME];
        delete newValidation[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME];
      }

      const isAllowSubmit =
        Object.values(newInformation).every((value) => value) &&
        Object.values(newValidation).every((value) => !value);

      if (isAllowSubmit) {
        if (isPreventNextStep2) {
          setChecking(true);
          return;
        }

        if (!isAddAnotherReceivingCurrency) {
          dispatch({
            type: SET_ADD_TRANSACTION_INFORMATION,
            payload: {
              ...addTransactionInformation,
              [ADD_TRANSACTION_STEP_REFERENCE[currentStep]]: {
                ...information,
                isAddAnotherReceivingCurrency,
                currentCurrencySelected,
                currentCurrencySelectedAnother: null,
                currentRateStore,
                currentRateAnotherStore: null,
                feeAmount,
              },
            },
          });
        } else {
          dispatch({
            type: SET_ADD_TRANSACTION_INFORMATION,
            payload: {
              ...addTransactionInformation,
              [ADD_TRANSACTION_STEP_REFERENCE[currentStep]]: {
                ...information,
                isAddAnotherReceivingCurrency,
                currentCurrencySelected,
                currentCurrencySelectedAnother,
                currentRateStore,
                currentRateAnotherStore,
                feeAmount,
              },
            },
          });
        }

        handleOnClickDiscountEligibilityToastCloseButton();
        dispatch({
          type: SET_ADD_TRANSACTION_CURRENT_STEP,
          payload: currentStep + 1,
        });
      } else {
        if (
          !newValidation[ADD_TRANSACTION_YOU_SEND_NAME] &&
          !newValidation[ADD_TRANSACTION_RECEIVER_GET_NAME] &&
          !newValidation[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] &&
          !newValidation[ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME]
        ) {
          ADD_TRANSACTION_AMOUNT_FIELDS.forEach((field) => {
            newValidation = checkFormInputValidation(
              information[field.name],
              field,
              newValidation,
            );
          });

          if (isAddAnotherReceivingCurrency) {
            ADD_TRANSACTION_AMOUNT_ANOTHER_FIELDS.forEach((field) => {
              newValidation = checkFormInputValidation(
                information[field.name],
                field,
                newValidation,
              );
            });
          }

          setValidation(newValidation);
        }
      }
    };

    const handleBackStep = () => {
      handleOnClickDiscountEligibilityToastCloseButton();
      dispatch({
        type: SET_ADD_TRANSACTION_CURRENT_STEP,
        payload: currentStep - 1,
      });
    };

    const renderCalculatorFormMobile = () => {
      if (isDesktop) return null;
      if (isEmpty(countryConfig)) return null;
      if (!isShowCalculatorForm) return null;
      return (
        <>
          <CalculatorForm
            ref={validRef}
            information={information}
            countryConfig={countryConfig}
            setFetching={setFetching}
            isDiscountSettled={isDiscountSettled}
            isCalculatorFormAccordion={true}
            styleWrapperBodyContent={{ borderRadius: '10px 10px 0 0' }}
            styleAccordionButton={{ borderRadius: '0 0 10px 10px' }}
            isExpandDefault
            isForceCalculatorFormAccordionExpand
          />
        </>
      );
    };
    const renderDiscountEligibilityToastMobile = () => {
      if (isDesktop) return null;
      if (isEmpty(countryConfig)) return null;
      if (!isShowCalculatorForm) return null;
      return (
        <>
          {!isEligibilityNudgeToast &&
            renderDiscountEligibilityToast({
              discountApplyFailToastCode: failedDiscount?.code,
              discountEligibilityToastType,
              onDiscountApplyFailToastAction: handleOpenFailedDiscountConditions,
              customStyles: {
                position: 'absolute',
                zIndex: '1',
                top: '127px',
                width: 'calc(100% - 32px)',
                left: '16px',
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.12)',
              },
            })}
        </>
      );
    };

    const renderCalculatorFormDesktop = () => {
      if (!isDesktop) return null;
      if (isEmpty(countryConfig)) return null;
      if (!isShowCalculatorForm) return null;
      return (
        <>
          <CalculatorForm
            information={information}
            countryConfig={countryConfig}
            setFetching={setFetching}
            isDiscountSettled={isDiscountSettled}
          />
          {!isEligibilityNudgeToast &&
            renderDiscountEligibilityToast({
              discountApplyFailToastCode: failedDiscount?.code,
              discountEligibilityToastType,
              onDiscountApplyFailToastAction: handleOpenFailedDiscountConditions,
              customStyles: {
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.12)',
              },
            })}
        </>
      );
    };

    const renderDiscountSection = () => {
      // Make Payment: discount recovery/auto-apply are disabled (isDisabled), so
      // isCurrentDiscountsLoaded never turns true and the section below would not
      // render. But the created txn's applied discount is hydrated in the store
      // and untouched (recovery is off), so show it read-only here to match the
      // create-transaction flow. Plain DiscountsDrawer (not the auto-apply Ext);
      // nothing is applyable and the remove button auto-hides under isMakePayment.
      if (isMakePayment) {
        if (isEmpty(discountApplied)) return null;
        return (
          <>
            <DiscountWrapper>
              <DiscountsDrawer
                onApplyError={() => {}}
                isDiscountResultReady={true}
              />
            </DiscountWrapper>
            <SizeBox height={32} />
          </>
        );
      }
      // DEV-1320: also render when the active-list fetch failed. On a failure
      // the store's discountFetchKey is never written, so isCurrentDiscountsLoaded
      // stays false; without this the drawer would unmount and FailureSection
      // could never appear after the system-error modal closes.
      if (!isCurrentDiscountsLoaded && !hasActiveFetchFailed) return null;
      return (
        <>
          <DiscountWrapper>
            <DiscountsDrawerExt
              autoApplyPolicy={autoApplyPolicy}
              handleApplyDiscount={handleApplyDiscount}
              failedDiscountRecovery={{
                canViewConditions: canViewFailedDiscountConditions,
                discount: failedDiscount,
                errorCode: failedDiscountErrorCode,
                isApplying: isApplyingDiscount,
                // DEV-1320: a context-change revalidation is carried but its
                // apply has not started yet (isApplyingDiscount still false),
                // while the stale discountApplied is retained in the store. Fold
                // isRevalidationPending into the drawer's validating/loading gate
                // so the DiscountLoading overlay covers that window instead of
                // briefly painting the old-amount applied badge; isApplyingDiscount
                // takes over (via its min-visible hold) once the apply fires, so
                // there is no gap. Mirrors the calculator line and eligibility
                // toast, which already gate on isRevalidationPending.
                isValidating:
                  isDefaultDiscountApplyPending || isRevalidationPending,
                onRetry: handleRetryFailedDiscount,
                onViewConditions: handleOpenFailedDiscountConditions,
              }}
              activeListFetchFailure={{
                isVisible: hasActiveFetchFailed,
                onRetry: onRetryActiveListFetch,
              }}
              isDisabled={isDisabled || !isShowCalculatorForm}
              // isDiscountResultReady latches true only after a successful load;
              // OR the fetch-failure flag so FailureSection can reveal even when
              // the first load failed (nothing ever latched it true).
              isDiscountResultReady={isDiscountResultReady || hasActiveFetchFailed}
              isEmptyVisible={true}
              onDrawerOpen={() => setShowDiscountEligibilityToast(false)}
            />
          </DiscountWrapper>
          <SizeBox height={32} />
        </>
      );
    };

    useEffect(() => {
      if (isMakePayment) {
        handleOnClickDiscountEligibilityToastCloseButton();
        setShowCalculatorForm(true);
      }
    }, [isMakePayment]);

    useAutoShowEventModal({
      screenId: SCREEN_ID,
      params: [],
      modalStyles: {},
      modalOptions: {},
    });

    return (
      <>
        {isOpenHolidayUrgentPopup && (
          <HolidayUrgentPopup
            isOpen={isOpenHolidayUrgentPopup}
            setOpen={setOpenHolidayUrgentPopup}
          />
        )}
        {isOpenFailedDiscountModal && failedDiscount && (
          <DiscountModal
            isOpen={isOpenFailedDiscountModal && failedDiscount}
            discount={failedDiscount}
            onClose={handleCloseFailedDiscountConditions}
            inactive
          />
        )}
        <BodySectionHeader
          titleBodySection={
            isMakePayment
              ? t('add_transaction_title_make_payment')
              : t('add_transaction_title_new_transaction')
          }
          titleSection={t('add_transaction_title_remittance')}
          CalculatorComponent={renderCalculatorFormDesktop()}
          onToggleCancelPopup={handleOnToggleCancelPopup}
        />
        {renderDiscountEligibilityToastMobile()}
        <RemittanceStyled
          data-testid="e003-amount-quote-context"
          data-country-config-ready={
            countryConfig?.countryCode ? 'ready' : 'missing'
          }
          data-payout-method-ready={payoutMethod ? 'ready' : 'missing'}
        >
          {ADD_TRANSACTION_AMOUNT_FIELDS.map((field) => (
            <FormInputAmountValidation
              key={field.name}
              ref={formInputAmountRef}
              field={field}
              countryConfig={countryConfig}
              receiveLimits={receiveLimits}
              information={information}
              setInformation={setInformation}
              validation={validation}
              setValidation={setValidation}
              currentRateValue={currentRateValue}
              setCurrentRateValue={setCurrentRateValue}
              currentCurrencySelected={currentCurrencySelected}
              isDisabled={isDisabled || isDiscountLoaderVisible}
              isFetching={isFetching}
              setFetching={setFetching}
              isChecking={isChecking}
              setChecking={setChecking}
              onAmountChangeStart={handleDiscountAmountChangeStart}
              isShowCalculatorForm={isShowCalculatorForm}
              setShowCalculatorForm={setShowCalculatorForm}
            />
          ))}
          {/* {isShowToggleButtonToAddAnotherCurrency && (
            <>
              <FormInputAddAnotherReceivingCurrencySwitch
                countryConfig={countryConfig}
                information={information}
                setInformation={setInformation}
                validation={validation}
                setValidation={setValidation}
                receiveLimits={receiveLimits}
                isFetching={isFetching}
                setFetching={setFetching}
                isChecking={isChecking}
                setChecking={setChecking}
                isDisabled={isDisabled}
              />
            </>
          )} */}
          {/* {isAddAnotherReceivingCurrency && (
            <>
              {ADD_TRANSACTION_AMOUNT_ANOTHER_FIELDS.map((field) => (
                <FormInputAmountAnotherValidation
                  key={field.name}
                  ref={formInputAmountAnotherRef}
                  field={field}
                  countryConfig={countryConfig}
                  receiveLimits={receiveLimits}
                  information={information}
                  setInformation={setInformation}
                  validation={validation}
                  setValidation={setValidation}
                  currentRateValue={currentRateValueAnother}
                  setCurrentRateValue={setCurrentRateValueAnother}
                  currentCurrencySelected={currentCurrencySelectedAnother}
                  isDisabled={isDisabled}
                  isFetching={isFetching}
                  setFetching={setFetching}
                />
              ))}
            </>
          )} */}
          {renderDiscountSection()}
          {renderCalculatorFormMobile()}
          <NextWrap>
            <ActionWrapper>
              <ButtonCommonWrap>
                <ButtonCommon
                  value={t('button_back')}
                  onClick={handleBackStep}
                  styles={{
                    margin: '0px',
                    width: !isDesktop && '100%',
                  }}
                  color="var(--ds-c-blue)"
                  background="var(--ds-c-white)"
                  borderNameVariable={isDesktop && '--border-filter'}
                  isFill={false}
                />
              </ButtonCommonWrap>
              <ButtonCommon
                value={t('button_next')}
                color="var(--c-primary)"
                background="var(--bg-primary)"
                isFill={true}
                styles={{
                  margin: '0px',
                  width: '50%',
                }}
                onClick={handleNextStep}
                isDisabled={isNextDisabled}
                isUseKeyDown={!isNextDisabled}
              />
            </ActionWrapper>
          </NextWrap>
        </RemittanceStyled>
      </>
    );
  },
);

const RemittanceStyled = styled.div`
  display: inline-block;
  width: 100%;
  margin-top: -8px;
  padding-inline: 0px;
  padding-bottom: 140px;
  background: var(--ds-c-white);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 437px;
    margin-top: 0px;
    padding-inline: 0px;
    padding-bottom: unset;
  }
`;

const DiscountWrapper = styled.div`
  margin-top: 16px;
`;

export const NextWrap = styled.div`
  position: fixed;
  bottom: 0;
  z-index: 1;
  width: 100%;
  height: fit-content;
  margin-inline: -16px;
  padding-inline: 0px;
  background: transparent;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    position: unset;
    margin-top: 40px;
    margin-bottom: 24px;
    margin-inline: unset;
    padding-inline: unset;
    width: 437px;
    height: fit-content;
    border-top: unset;
  }
`;
export const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 77px;
  width: 100%;
  background: var(--ds-c-white);
  border-top: 1px solid var(--ds-bg-2);
  padding-inline: 16px;
  margin: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 100%;
    height: 44px;
    border-top: unset;
    padding-inline: unset;
    margin: 0px;
  }
`;
export const ButtonCommonWrap = styled.div`
  width: 50%;
`;

export default Remittance;
